import React, { forwardRef, useMemo } from 'react';
import { Col, Form, FormInstance, Grid, Row } from 'rsuite';
import FormTextField from './FormTextField';
import UnrestrictedAddressLookup from './UnrestrictedAddressLookup';
import CityRestrictedAddressLookup from './CityRestrictedAddressLookup';
import { FORM_INPUT_TYPES } from '../../common/enums';
import {
  convertSnakeCaseToCamelCase,
  objectHasProperty,
} from '../../common/utils';
import AnnotatedCheckbox from './AnnotatedCheckbox';
import { Schema } from 'schema-typed';
import { FormModelPerson } from 'src/types/person';
export interface PersonDetailsProps {
  id: string;
  formError: Record<string, any>;
  formModel?: Schema<FormModelPerson, string>;
  formValues: FormModelPerson;
  onSameAsPropertyAddressChanged?: (value: boolean) => void;
  optionalFields?: Record<string, unknown>;
  restrictAddressToCity?: boolean;
  setFormError: (value: Record<string, any>) => void;
  setFormValues: (value: FormModelPerson) => void;
  showEmail?: boolean;
  showSameAsPropertyAddressCheckboxOption?: boolean;
  validationModel: Record<string, any>;
  disabled?: boolean;
  disabledFields?: Partial<Record<keyof FormModelPerson, boolean>>;
}

const PersonDetails = forwardRef<FormInstance, PersonDetailsProps>(
  (
    {
      id,
      formError,
      formModel,
      formValues,
      onSameAsPropertyAddressChanged = (): void => {},
      optionalFields = {},
      restrictAddressToCity = false,
      setFormError,
      setFormValues,
      showEmail = true,
      showSameAsPropertyAddressCheckboxOption = false,
      validationModel,
      disabledFields = {},
      disabled = false,
    }: PersonDetailsProps,
    ref
  ) => {
    const parsedOptionalFields = useMemo((): Record<string, string> => {
      if (!optionalFields) {
        return {};
      }
      return Object.keys(optionalFields).reduce(
        (prev: Record<string, string>, value: string) => {
          const key = convertSnakeCaseToCamelCase(value);
          prev[key] = optionalFields[value] as string;
          return prev;
        },
        {}
      );
    }, [optionalFields]);

    const onAddressTextChanged = (): void => {
      onSameAsPropertyAddressChanged(false);
    };

    const formChange = (values: Record<string, string>) =>
      setFormValues({ ...formValues, ...values });

    const hasSingleDisabledFields = useMemo(() => {
      return Object.values(disabledFields).filter((value) => value).length > 0;
    }, [disabledFields]);

    return (
      <Form
        model={formModel}
        ref={ref}
        checkTrigger="none"
        formDefaultValue={formValues}
        formValue={formValues}
        onCheck={setFormError}
        onChange={formChange}
        fluid
      >
        <Grid fluid>
          <Row
            gutter={24}
            className="mb-12"
          >
            <Col
              xs={24}
              md={12}
              className="sm-pb-12"
            >
              <FormTextField
                data-cy="person-first-name"
                name="firstName"
                label={`First Name${objectHasProperty('firstName', parsedOptionalFields) && parsedOptionalFields['firstName'] === 'optional' ? ' (optional)' : ''}`}
                hasError={formError['firstName']}
                validationRule={validationModel.firstName}
                errorMessage={formError['firstName']}
                disabled={
                  disabledFields.firstName ||
                  (!hasSingleDisabledFields && disabled)
                }
                value={formValues?.firstName ?? ''}
              />
            </Col>
            <Col
              xs={24}
              md={12}
            >
              <FormTextField
                data-cy="person-last-name"
                name="lastName"
                label={`Last Name${objectHasProperty('lastName', parsedOptionalFields) && parsedOptionalFields['lastName'] === 'optional' ? ' (optional)' : ''}`}
                hasError={formError['lastName']}
                validationRule={validationModel.lastName}
                errorMessage={formError['lastName']}
                disabled={
                  disabledFields.lastName ||
                  (!hasSingleDisabledFields && disabled)
                }
                value={formValues?.lastName ?? ''}
              />
            </Col>
          </Row>
          <Row
            gutter={24}
            className="mb-12"
          >
            <Col xs={24}>
              <FormTextField
                data-cy="person-company"
                name="companyName"
                label="Company Name (optional)"
                hasError={formError['companyName']}
                validationRule={validationModel.companyName}
                value={formValues?.companyName ?? ''}
                errorMessage={formError['companyName']}
                disabled={
                  disabledFields.companyName ||
                  (!hasSingleDisabledFields && disabled)
                }
              />
            </Col>
          </Row>
          {showEmail && (
            <Row
              gutter={24}
              className="mb-12"
            >
              <Col xs={24}>
                <FormTextField
                  data-cy="person-email"
                  name="email"
                  label={`Email Address${objectHasProperty('email', parsedOptionalFields) && parsedOptionalFields['email'] === 'optional' ? ' (optional)' : ''}`}
                  hasError={formError['email']}
                  validationRule={validationModel.email}
                  errorMessage={formError['email']}
                  disabled={
                    disabledFields.email ||
                    (!hasSingleDisabledFields && disabled)
                  }
                  value={formValues?.email ?? ''}
                  type={FORM_INPUT_TYPES.EMAIL}
                />
              </Col>
            </Row>
          )}
          <Row
            gutter={24}
            className="mb-12"
          >
            <Col xs={24}>
              <FormTextField
                data-cy="person-phone-number"
                name="phone"
                label={`Phone Number${objectHasProperty('phone', parsedOptionalFields) && parsedOptionalFields['phone'] === 'optional' ? ' (optional)' : ''}`}
                hasError={formError['phone']}
                validationRule={validationModel.phone}
                errorMessage={formError['phone']}
                type={FORM_INPUT_TYPES.PHONE}
                disabled={
                  disabledFields.phone || (!hasSingleDisabledFields && disabled)
                }
                value={formValues?.phone ?? ''}
              />
            </Col>
          </Row>
          <Row gutter={24}>
            <Col
              xs={24}
              className={showSameAsPropertyAddressCheckboxOption ? '' : 'mb-12'}
              data-cy="person-mailing-address"
            >
              {restrictAddressToCity ? (
                <CityRestrictedAddressLookup
                  xs={24}
                  name="mailingAddress"
                  label={`Mailing Address${objectHasProperty('mailingAddress', parsedOptionalFields) && parsedOptionalFields['mailingAddress'] === 'optional' ? ' (optional)' : ''}`}
                  hasError={formError['mailingAddress']}
                  validationRule={validationModel.mailingAddress}
                  errorMessage="Address is required"
                  checkTrigger="none"
                  disabled={
                    disabledFields.mailingAddress ||
                    (!hasSingleDisabledFields && disabled)
                  }
                  onTextChanged={onAddressTextChanged}
                  value={formValues?.mailingAddress ?? ''}
                />
              ) : (
                <UnrestrictedAddressLookup
                  xs={24}
                  name="mailingAddress"
                  label={`Mailing Address${objectHasProperty('mailingAddress', parsedOptionalFields) && parsedOptionalFields['mailingAddress'] === 'optional' ? ' (optional)' : ''}`}
                  hasError={formError['mailingAddress']}
                  validationRule={validationModel.mailingAddress}
                  errorMessage="Address is required"
                  checkTrigger="none"
                  disabled={
                    disabledFields.mailingAddress ||
                    (!hasSingleDisabledFields && disabled)
                  }
                  value={formValues?.mailingAddress ?? ''}
                  onTextChanged={onAddressTextChanged}
                />
              )}
            </Col>
          </Row>
          {showSameAsPropertyAddressCheckboxOption && (
            <Row
              className="mt-4"
              gutter={24}
            >
              <Col
                xs={24}
                className="mb-12"
              >
                <AnnotatedCheckbox
                  id={id}
                  label="Same as property address"
                  name="sameAsPropertyAddress"
                  value={!!formValues?.sameAsPropertyAddress}
                  onChange={onSameAsPropertyAddressChanged}
                  disabled={
                    disabledFields.sameAsPropertyAddress ||
                    (!hasSingleDisabledFields && disabled)
                  }
                />
              </Col>
            </Row>
          )}
        </Grid>
      </Form>
    );
  }
);

export default PersonDetails;
